import { useEffect, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { LoadingScreen, LoginScreen, MissingDirectoryScreen } from './components/AuthScreens';
import { ChatPanel } from './components/ChatPanel';
import { ErrorBoundary } from './components/ErrorBoundary';
import { NavigationSidebar, type AppTab } from './components/NavigationSidebar';
import { OverviewPanel } from './components/OverviewPanel';
import { AppFrame, MainContent, SidebarColumn } from './components/Shell';
import { SelectedContextBar } from './components/SelectedContextBar';
import { Topbar } from './components/Topbar';
import { Card, StatusPill } from './components/ui';
import * as api from './lib/api';
import { normalizeHistory, partsToText } from './lib/messages';
import { GlobalStyle, themes } from './styles/theme';
import type { ApiError, AuthUser, ChatMessage, OpencodeStatus, SelectedGlobalContext, Session, ThemeMode } from './types';
import styled from 'styled-components';

const ErrorBanner = styled(Card)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  background: ${p => p.theme.dangerSoft};
  border-color: ${p => p.theme.danger};
  color: ${p => p.theme.dangerStrong};
`;

const BannerText = styled.div`
  font-size: 14px;
  line-height: 1.4;
  strong { display: block; margin-bottom: 4px; }
  code { background: rgba(0,0,0,0.07); padding: 2px 6px; border-radius: 6px; }
`;

function getInitialTheme(): ThemeMode {
  const saved = window.localStorage.getItem('blanketchat-theme');
  if (saved === 'light' || saved === 'dark') return saved;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function errorMessage(error: unknown): string {
  const err = error as ApiError;
  return err.response?.data?.error || err.response?.data?.message || err.message || 'Unexpected error';
}

function storageUserId(user: AuthUser): string {
  return user.id || user.email || user.username || 'unknown';
}

function contextStorageKey(userId: string): string {
  return `blanketchat-global-context:${userId}`;
}

function sessionContextStorageKey(userId: string): string {
  return `blanketchat-session-context:${userId}`;
}

function readStoredJson<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : fallback;
  } catch {
    return fallback;
  }
}

function App() {
  const [themeMode, setThemeMode] = useState<ThemeMode>(getInitialTheme);
  const [authChecked, setAuthChecked] = useState(false);
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [loginIdentity, setLoginIdentity] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [selectedContext, setSelectedContext] = useState<SelectedGlobalContext | null>(null);
  const [sessionContexts, setSessionContexts] = useState<Record<string, SelectedGlobalContext>>({});
  const [storageReadyUser, setStorageReadyUser] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [opencodeStatus, setOpencodeStatus] = useState<OpencodeStatus>({ checked: false, ok: null, url: null, error: null });
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<AppTab>('overview');
  const sessionsInFlight = useRef(false);
  const historyInFlight = useRef(false);

  const currentDirectory = typeof authUser?.directory === 'string' ? authUser.directory.trim() : '';
  const currentStorageUser = authUser ? storageUserId(authUser) : '';
  const sessionsWithContext = sessions.map(session => ({ ...session, context: session.context || sessionContexts[session.id] }));
  const theme = themes[themeMode];

  useEffect(() => {
    window.localStorage.setItem('blanketchat-theme', themeMode);
  }, [themeMode]);

  useEffect(() => {
    let active = true;
    api.getCurrentUser()
      .then(response => { if (active) setAuthUser(response.record); })
      .catch(() => { if (active) setAuthUser(null); })
      .finally(() => { if (active) setAuthChecked(true); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!authUser || !currentDirectory) return;
    setSelectedSession(null);
    setMessages([]);
    checkOpencode();
    fetchSessions();
  }, [authUser, currentDirectory]);

  useEffect(() => {
    if (!authUser || !currentStorageUser) {
      setSelectedContext(null);
      setSessionContexts({});
      setStorageReadyUser('');
      return;
    }
    setSelectedContext(readStoredJson<SelectedGlobalContext | null>(contextStorageKey(currentStorageUser), null));
    setSessionContexts(readStoredJson<Record<string, SelectedGlobalContext>>(sessionContextStorageKey(currentStorageUser), {}));
    setStorageReadyUser(currentStorageUser);
  }, [authUser, currentStorageUser]);

  useEffect(() => {
    if (!currentStorageUser || storageReadyUser !== currentStorageUser) return;
    const key = contextStorageKey(currentStorageUser);
    if (selectedContext) window.localStorage.setItem(key, JSON.stringify(selectedContext));
    else window.localStorage.removeItem(key);
  }, [currentStorageUser, storageReadyUser, selectedContext]);

  useEffect(() => {
    if (!currentStorageUser || storageReadyUser !== currentStorageUser) return;
    window.localStorage.setItem(sessionContextStorageKey(currentStorageUser), JSON.stringify(sessionContexts));
  }, [currentStorageUser, storageReadyUser, sessionContexts]);

  useEffect(() => {
    if (!authUser || !currentDirectory || !selectedSession) {
      setMessages([]);
      return;
    }
    loadHistory(selectedSession.id);
  }, [authUser, currentDirectory, selectedSession?.id]);

  useEffect(() => {
    let active = true;
    const tick = async () => {
      if (!active || !authUser || !currentDirectory || document.hidden) return;
      if (!sessionsInFlight.current) {
        sessionsInFlight.current = true;
        api.listSessions()
          .then(list => { if (active) setSessions(list); })
          .catch(error => { if (active) handleAuthExpired(error); })
          .finally(() => { sessionsInFlight.current = false; });
      }
      if (selectedSession?.id && !isLoading) {
        loadHistory(selectedSession.id, { showSpinner: false });
      }
    };
    const interval = window.setInterval(tick, 2000);
    return () => { active = false; window.clearInterval(interval); };
  }, [authUser, currentDirectory, selectedSession?.id, isLoading]);

  function toggleTheme() {
    setThemeMode(mode => (mode === 'light' ? 'dark' : 'light'));
  }

  function handleAuthExpired(error: unknown) {
    const err = error as ApiError;
    if (err.response?.status === 401) {
      setAuthUser(null);
      setAuthError('Session expired. Log in again.');
    }
  }

  async function handleLogin(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsLoggingIn(true);
    setAuthError('');
    try {
      const response = await api.login(loginIdentity, loginPassword);
      setAuthUser(response.record);
      setLoginPassword('');
    } catch (error) {
      setAuthError(errorMessage(error));
    } finally {
      setIsLoggingIn(false);
    }
  }

  async function handleLogout() {
    await api.logout().catch(() => undefined);
    setAuthUser(null);
    setSessions([]);
    setSelectedSession(null);
    setSelectedContext(null);
    setSessionContexts({});
    setStorageReadyUser('');
    setMessages([]);
  }

  async function checkOpencode() {
    if (!currentDirectory) return;
    try {
      const status = await api.getHealth();
      setOpencodeStatus(status);
    } catch (error) {
      handleAuthExpired(error);
      const err = error as ApiError;
      setOpencodeStatus({
        checked: true,
        ok: false,
        url: err.response?.data?.url || null,
        error: errorMessage(error),
      });
    }
  }

  async function fetchSessions() {
    if (!currentDirectory) return;
    try {
      setSessions(await api.listSessions());
    } catch (error) {
      handleAuthExpired(error);
      console.error('Error fetching sessions:', error);
    }
  }

  async function loadHistory(sessionId: string, { showSpinner = true } = {}) {
    if (!sessionId || !currentDirectory || historyInFlight.current) return;
    historyInFlight.current = true;
    if (showSpinner) setIsHistoryLoading(true);
    try {
      const history = await api.listMessages(sessionId);
      setMessages(normalizeHistory(history));
    } catch (error) {
      handleAuthExpired(error);
      setMessages([{ id: 'hist-err', role: 'assistant', content: `Error loading history: ${errorMessage(error)}`, timestamp: Date.now() }]);
    } finally {
      historyInFlight.current = false;
      if (showSpinner) setIsHistoryLoading(false);
    }
  }

  async function handleCreateSession(name: string) {
    if (!currentDirectory) return;
    try {
      const created = await api.createSession(name, selectedContext);
      if (created?.id) {
        setSessions(prev => [...prev, created]);
        if (selectedContext) {
          setSessionContexts(prev => ({ ...prev, [created.id]: selectedContext }));
        }
      }
    } catch (error) {
      handleAuthExpired(error);
      console.error('Error creating session:', error);
    }
  }

  function handleSelectSession(session: Session) {
    setActiveTab('ai');
    setSelectedSession(session);
    const storedContext = sessionContexts[session.id] || session.context;
    if (storedContext) setSelectedContext(storedContext);
  }

  async function handleDeleteSession(id: string) {
    if (!currentDirectory) return;
    try {
      await api.deleteSession(id);
      setSessions(prev => prev.filter(session => session.id !== id));
      setSessionContexts(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      if (selectedSession?.id === id) {
        setSelectedSession(null);
        setMessages([]);
      }
    } catch (error) {
      handleAuthExpired(error);
      console.error('Error deleting session:', error);
    }
  }

  async function handleSendMessage() {
    if (!input.trim() || !selectedSession || !currentDirectory || selectedSession.parentID) return;
    const outgoing = input;
    const assistantId = `assistant-${Date.now()}`;
    setMessages(prev => [...prev, { content: outgoing, role: 'user', timestamp: Date.now() }, { id: assistantId, content: '', role: 'assistant', timestamp: Date.now() }]);
    setInput('');
    setIsLoading(true);
    try {
      const response = await api.sendChatMessage(selectedSession.id, outgoing);
      const text = partsToText(response.parts) || '(empty response)';
      setMessages(prev => prev.map(message => message.id === assistantId ? { ...message, content: text } : message));
    } catch (error) {
      handleAuthExpired(error);
      setMessages(prev => prev.map(message => message.id === assistantId ? { ...message, content: `Error: ${errorMessage(error)}` } : message));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      {!authChecked && <LoadingScreen themeMode={themeMode} onToggleTheme={toggleTheme} />}
      {authChecked && !authUser && (
        <LoginScreen
          themeMode={themeMode}
          onToggleTheme={toggleTheme}
          identity={loginIdentity}
          password={loginPassword}
          error={authError}
          isLoading={isLoggingIn}
          onIdentityChange={setLoginIdentity}
          onPasswordChange={setLoginPassword}
          onSubmit={handleLogin}
        />
      )}
      {authChecked && authUser && !currentDirectory && <MissingDirectoryScreen themeMode={themeMode} onToggleTheme={toggleTheme} onLogout={handleLogout} />}
      {authChecked && authUser && currentDirectory && (
        <AppFrame>
          <Topbar
            user={authUser}
            directory={currentDirectory}
            themeMode={themeMode}
            onSelectContext={setSelectedContext}
            onToggleTheme={toggleTheme}
            onLogout={handleLogout}
          />
          <SelectedContextBar context={selectedContext} onSelectContext={setSelectedContext} onClear={() => setSelectedContext(null)} />
          <SidebarColumn>
            <NavigationSidebar
              activeTab={activeTab}
              sessions={sessionsWithContext}
              selectedSession={selectedSession}
              status={opencodeStatus}
              onTabChange={setActiveTab}
              onCreateSession={handleCreateSession}
              onDeleteSession={handleDeleteSession}
              onSelectSession={handleSelectSession}
            />
          </SidebarColumn>
          <MainContent>
            {opencodeStatus.checked && !opencodeStatus.ok && (
              <ErrorBanner>
                <BannerText>
                  <strong>Cannot reach opencode</strong>
                  {opencodeStatus.url && <>URL: <code>{opencodeStatus.url}</code><br /></>}
                  Error: {opencodeStatus.error}
                </BannerText>
                <StatusPill>Offline</StatusPill>
              </ErrorBanner>
            )}
            {activeTab === 'overview' ? (
              <OverviewPanel context={selectedContext} sessions={sessionsWithContext} status={opencodeStatus} />
            ) : (
              <ChatPanel
                selectedSession={selectedSession}
                messages={messages}
                input={input}
                isLoading={isLoading}
                isHistoryLoading={isHistoryLoading}
                onInputChange={setInput}
                onSend={handleSendMessage}
              />
            )}
          </MainContent>
        </AppFrame>
      )}
    </ThemeProvider>
  );
}

export default function AppWithBoundary() {
  return <ErrorBoundary><App /></ErrorBoundary>;
}
